import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import type { SwapOffer, SwapSide, SwapStatus } from '@keetcheck/shared';
import { alpha, theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useMyShifts, useSwapOptions, useCreateSwap, useSwap, useSwapAction } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { Pill } from '../components/CoverBits.js';
import { emitError } from '../lib/toastBus.js';
import { KIND_LABEL, rangeLabel } from '../lib/shifts.js';

const sideLabel = (s: SwapSide) => `${KIND_LABEL[s.kind]} · ${rangeLabel(s.startsAt, s.endsAt)}`;
const sameSide = (a: SwapSide | null, b: SwapSide | null) => !!a && !!b && a.kind === b.kind && a.startsAt === b.startsAt && a.endsAt === b.endsAt;

// ── Offer a swap ───────────────────────────────────────────────

/** Opened from My shifts with ?shift=<id>: pick who, and which of their shifts you'd do. */
export function SwapNewPage() {
  const nav = useNavigate();
  const back = useBack('/shifts');
  const [params] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: mine = [] } = useMyShifts();
  const shift = mine.find((s) => s.id === params.get('shift'));
  const give: SwapSide | null = shift ? { kind: shift.kind as SwapSide['kind'], startsAt: shift.startsAt, endsAt: shift.endsAt } : null;
  const { data: opts, isLoading } = useSwapOptions(orgId, give);
  const create = useCreateSwap(orgId);
  const [who, setWho] = useState<string | null>(null);
  const [want, setWant] = useState<SwapSide | null>(null); // null + who = "let them pick"
  const [note, setNote] = useState('');

  async function send() {
    if (!give || !who) return;
    try {
      const r = await create.mutateAsync({ targetId: who, give, want, note: note.trim() || null });
      nav(`/swap/${r.id}`, { replace: true });
    } catch (e) {
      emitError(e instanceof Error ? e.message : 'Could not send the offer');
    }
  }

  return (
    <PageShell noNav>
      <Header title="Offer a swap" onBack={back} />
      <div style={body}>
        {!give && <div style={muted}>{mine.length ? "That shift isn't one of yours any more." : 'Loading…'}</div>}
        {give && (
          <>
            <Card>
              <div style={label}>You give</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: theme.text }}>{sideLabel(give)}</div>
            </Card>
            {isLoading && <div style={muted}>Finding people you could swap with…</div>}
            {opts && !opts.valid && <div style={{ ...banner, background: theme.status.danger.bg, color: theme.status.danger.fg }}>{opts.error}</div>}
            {opts?.valid && opts.candidates.length === 0 && (
              <div style={muted}>Nobody who can do this shift has a shift you could do in return. Try asking for cover instead.</div>
            )}
            {opts?.valid && opts.candidates.length > 0 && (
              <Card>
                <div style={label}>Swap with</div>
                {opts.candidates.map((c) => {
                  const open = who === c.userId;
                  return (
                    <div key={c.userId} style={{ borderTop: `1px solid ${theme.border}`, padding: '8px 0' }}>
                      <button onClick={() => { setWho(open ? null : c.userId); setWant(null); }} style={rowBtn}>
                        <span style={{ flex: 1 }}>
                          <strong style={{ color: theme.text }}>{c.displayName}</strong>
                          <span style={{ color: theme.textMuted, fontSize: 12 }}> {c.teamName ?? ''} · {c.theirShifts.length} shift{c.theirShifts.length > 1 ? 's' : ''} you could do</span>
                        </span>
                        <Icon name={open ? 'chevronD' : 'chevron'} size={14} />
                      </button>
                      {open && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                          <div style={{ fontSize: 12, color: theme.textMuted }}>…and you do:</div>
                          <button onClick={() => setWant(null)} style={choice(want === null)}>Let {c.displayName} pick</button>
                          {c.theirShifts.map((y) => (
                            <button key={`${y.kind}-${y.startsAt}`} onClick={() => setWant(y)} style={choice(sameSide(want, y))}>{sideLabel(y)}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </Card>
            )}
            {who && (
              <>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2}
                  placeholder="Note (optional)" aria-label="Note" style={{ ...input, width: '100%', resize: 'vertical' }} />
                <button disabled={create.isPending} onClick={() => void send()} style={primary}>
                  {create.isPending ? 'Sending…' : 'Send swap offer'}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}

// ── One swap ───────────────────────────────────────────────────

const STATUS: Record<SwapStatus, [string, 'ok' | 'warn' | 'danger' | 'muted']> = {
  pending: ['Waiting for an answer', 'warn'], accepted: ['Agreed', 'ok'], declined: ['Declined', 'danger'],
  cancelled: ['Withdrawn', 'muted'], expired: ['Expired', 'muted'], undone: ['Undone', 'muted'],
};

export function SwapStatusPill({ status }: { status: SwapStatus }) {
  const [text, tone] = STATUS[status];
  return <Pill tone={tone}>{text}</Pill>;
}

export function SwapDetailPage() {
  const nav = useNavigate();
  const { id = '' } = useParams();
  const back = useBack('/shifts?tab=cover');
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: s, isLoading, error } = useSwap(orgId, id);
  return (
    <PageShell noNav>
      <Header title="Swap" onBack={back} />
      <div style={body}>
        {isLoading && <div style={muted}>Loading…</div>}
        {error && <div style={muted}>{(error as Error).message}</div>}
        {s && <SwapDetail orgId={orgId} s={s} />}
      </div>
    </PageShell>
  );
}

function SwapDetail({ orgId, s }: { orgId: string; s: SwapOffer }) {
  const act = useSwapAction(orgId, s.id);
  const [pick, setPick] = useState<SwapSide | null>(null);
  const me = s.role;
  const other = me === 'proposer' ? s.target.displayName : s.proposer.displayName;
  // From the viewer's side: what I give away, what I take on.
  const iGive = me === 'target' ? s.want : s.give;
  const iTake = me === 'target' ? s.give : s.want;
  const run = (action: 'accept' | 'decline' | 'cancel', want?: SwapSide) =>
    act.mutate(want ? { action, want } : { action }, { onError: (e) => emitError(e instanceof Error ? e.message : 'Something went wrong') });

  return (
    <>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={label}>{me === 'viewer' ? `${s.proposer.displayName} ⇄ ${s.target.displayName}` : `With ${other}`}</span>
          <SwapStatusPill status={s.status} />
        </div>
        <Side title={me === 'viewer' ? `${s.target.displayName} does` : 'You take on'} side={iTake} placeholder={me === 'target' ? '' : `${other} picks one of theirs`} tone="ok" />
        <div style={{ textAlign: 'center', color: theme.textMuted, fontSize: 18, margin: '4px 0' }}>⇅</div>
        <Side title={me === 'viewer' ? `${s.proposer.displayName} does` : 'You hand over'} side={iGive} placeholder={me === 'target' ? 'You pick one of yours (below)' : ''} tone="muted" />
        {s.note && <div style={{ fontSize: 13, color: theme.text, marginTop: 10, padding: '8px 10px', background: theme.surface2, borderRadius: 8 }}>“{s.note}”</div>}
      </Card>

      {me === 'target' && s.status === 'pending' && (
        <Card>
          {!s.want && (
            <>
              <div style={label}>Which of your shifts does {s.proposer.displayName} do?</div>
              {(s.options ?? []).length === 0 && <div style={muted}>None of your shifts work for both of you any more.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                {(s.options ?? []).map((y) => (
                  <button key={`${y.kind}-${y.startsAt}`} onClick={() => setPick(y)} style={choice(sameSide(pick, y))}>{sideLabel(y)}</button>
                ))}
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={act.isPending || (!s.want && !pick)} onClick={() => run('accept', s.want ? undefined : pick!)} style={{ ...primary, flex: 2 }}>
              {act.isPending ? 'Saving…' : 'Accept swap'}
            </button>
            <button disabled={act.isPending} onClick={() => run('decline')} style={{ ...secondary, flex: 1 }}>Decline</button>
          </div>
        </Card>
      )}

      {me === 'proposer' && s.status === 'pending' && (
        <button disabled={act.isPending} onClick={() => run('cancel')} style={secondary}>Withdraw offer</button>
      )}
      {s.canUndo && (
        <button disabled={act.isPending} onClick={() => { if (confirm(`Undo the swap? ${other} will be told you're both back on your own shifts.`)) run('cancel'); }}
          style={{ ...secondary, color: theme.status.danger.fg }}>
          Undo swap
        </button>
      )}
      {s.status === 'accepted' && !s.canUndo && me !== 'viewer' && (
        <div style={muted}>Part of the swap has started, so it can't be undone here. Ask for cover if plans change.</div>
      )}
    </>
  );
}

function Side({ title, side, placeholder, tone }: { title: string; side: SwapSide | null; placeholder: string; tone: 'ok' | 'muted' }) {
  return (
    <div style={{ padding: '8px 10px', borderRadius: 8, background: theme.status[tone].bg }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: theme.status[tone].fg }}>{title}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>{side ? sideLabel(side) : placeholder}</div>
    </div>
  );
}

// ── bits ───────────────────────────────────────────────────────

function Header({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div style={{
      padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
      display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5,
    }}>
      <button onClick={onBack} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>
      <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>{title}</div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '12px 14px', borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}`, flexShrink: 0 }}>{children}</div>;
}

const body: React.CSSProperties = { flex: 1, overflowY: 'auto', background: theme.bg, padding: '14px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 };
const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 6, display: 'block' };
const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted, lineHeight: 1.5 };
const banner: React.CSSProperties = { padding: '10px 12px', borderRadius: 10, fontSize: 13 };
const input: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 14, boxSizing: 'border-box',
};
const rowBtn: React.CSSProperties = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0,
  cursor: 'pointer', fontFamily: theme.font, fontSize: 14, textAlign: 'left', color: theme.textDim,
};
const primary: React.CSSProperties = {
  padding: '12px 0', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 700,
  fontFamily: theme.font, background: theme.accent, color: theme.accentInk, cursor: 'pointer',
};
const secondary: React.CSSProperties = {
  padding: '11px 14px', borderRadius: 10, border: `1px solid ${theme.border}`, fontSize: 14, fontWeight: 700,
  fontFamily: theme.font, background: theme.surface, color: theme.text, cursor: 'pointer',
};
const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const choice = (on: boolean): React.CSSProperties => ({
  textAlign: 'left', padding: '9px 12px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
  border: `1px solid ${on ? theme.accent : theme.border}`, background: on ? alpha(theme.accent, 9) : theme.surface, color: theme.text,
});
